import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { StitchShell } from './components/StitchShell.jsx';
import { ApiError, api } from './lib/api.js';
import { SessionProvider } from './lib/session.jsx';
import type { Me } from './lib/types.js';
import { AdminPage } from './pages/AdminPage.js';
import { DocumentsPage } from './pages/DocumentsPage.js';
import { LogsPage } from './pages/LogsPage.jsx';
import { TemplatesPage } from './pages/TemplatesPage.jsx';
import { UsersPage } from './pages/UsersPage.js';
import { LoginPage } from './pages/LoginPage.js';

export function App() {
  const queryClient = useQueryClient();

  /**
   * `/api/auth/me` è anche il controllo di sessione: un 401 significa "non
   * autenticato" e non è un errore da mostrare, quindi non si ritenta.
   */
  const me = useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const logout = useMutation({
    mutationFn: () => api.logout(),
    // Anche se il logout fallisce lato server, la sessione locale va scartata.
    onSettled: () => queryClient.resetQueries(),
  });

  if (me.isPending) return <FullPage>Caricamento…</FullPage>;

  if (me.isError) {
    return (
      <FullPage>
        <div className="alert alert--error">
          {me.error instanceof ApiError ? me.error.message : 'Servizio non raggiungibile.'}
        </div>
        <button type="button" className="btn btn--ghost" onClick={() => void me.refetch()}>
          Riprova
        </button>
      </FullPage>
    );
  }

  if (!me.data) {
    return <LoginPage onSuccess={() => void queryClient.invalidateQueries({ queryKey: ['me'] })} />;
  }

  const isAdmin = me.data.user.isAdmin;

  return (
    <SessionProvider value={{ me: me.data, logout: () => logout.mutate() }}>
      <Routes>
        {/* Grafica unica: stitch. Layout (barra in alto) e Shell (sidebar
            navy) restano in albero ma non li monta piu' nessuno. */}
        <Route element={<StitchShell />}>
          <Route path="/documenti" element={<DocumentsPage />} />
          <Route
            path="/organizzazioni"
            // Il gate vero è server-side: qui si evita solo di mostrare una
            // pagina che risponderebbe 403 su ogni chiamata.
            element={isAdmin ? <AdminPage /> : <Navigate to="/documenti" replace />}
          />
          <Route
            path="/template"
            element={isAdmin ? <TemplatesPage /> : <Navigate to="/documenti" replace />}
          />
          <Route
            path="/utenti"
            element={isAdmin ? <UsersPage /> : <Navigate to="/documenti" replace />}
          />
          <Route
            path="/log"
            element={isAdmin ? <LogsPage /> : <Navigate to="/documenti" replace />}
          />
          <Route path="*" element={<Navigate to="/documenti" replace />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-6)',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </div>
  );
}
